var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DEFAULT_USER_SETTINGS } from '@/api/constants';
import { useApiInstance } from '@/api/index';
import { getCsrfHeaders } from '@/api/realApi/authApi';
import { useDeepCompareMemoize } from '@/hooks/useDeepCompareMemoize';
const AuthContext = createContext(undefined);
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [userSettings_, setUserSettings] = useState(DEFAULT_USER_SETTINGS);
    const userSettings = useDeepCompareMemoize(userSettings_);
    const api = useApiInstance();
    const apiLogin = api.authApiInstance.login;
    const apiLogout = api.authApiInstance.logout;
    const getUser = api.userApiInstance.getUser;
    const getUserSettings = api.userApiInstance.getUserSettings;
    const csrf = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        const csrfHeaders = yield getCsrfHeaders();
        const csrfToken = csrfHeaders.headers['X-CSRFToken'];
        return csrfToken;
    }), []);
    const fetchUser = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        setLoading(true);
        try {
            const res = yield getUser();
            if (res.id) {
                setUser(res);
            }
        }
        catch (e) {
            console.info('Error fetching user:', e);
            setUser(null);
        }
        finally {
            setLoading(false);
        }
    }), [getUser]);
    const fetchUserSettings = useCallback((roomCode) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const res = yield getUserSettings(roomCode);
            setUserSettings(res);
        }
        catch (e) {
            console.info('Error fetching user settings:', e);
        }
    }), [getUserSettings]);
    useEffect(() => {
        fetchUser();
        fetchUserSettings();
    }, [fetchUser, fetchUserSettings]);
    const login = useCallback((username, password) => __awaiter(void 0, void 0, void 0, function* () {
        const csrfToken = yield csrf();
        yield apiLogin(username, password, csrfToken);
        yield fetchUser();
    }), [apiLogin, csrf, fetchUser]);
    const logout = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield apiLogout();
            setUser(null);
        }
        catch (error) {
            setUser(null);
        }
    }), [apiLogout]);
    useEffect(() => { }, [loading]);
    const value = useMemo(() => ({
        user,
        loading,
        login,
        logout,
        fetchUser,
        userSettings,
        setUserSettings,
        fetchUserSettings,
    }), [user, loading, login, logout, fetchUser, userSettings, setUserSettings, fetchUserSettings]);
    return _jsx(AuthContext.Provider, { value: value, children: children });
};
export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx)
        throw new Error('useAuth must be used within AuthProvider');
    return ctx;
};
